import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import { isNotFoundError } from '@/shared/utils/httpErrors';
import type {
  TenantWorkScheduleDto,
  MemberWorkScheduleDto,
  MemberWorkScheduleDtoPagedResult,
  MemberWorkSchedulesResponse,
  MemberWorkSchedulesQueryParams,
  UpsertTenantWorkSchedulePayload,
  UpsertMemberWorkSchedulePayload,
} from '../types';
import { tenantWorkScheduleMock, memberWorkSchedulesMock } from './workSchedules.mock';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

export const workSchedulesService = {
  /**
   * Returns the tenant work schedule.
   * GET /api/work-schedules/tenant
   */
  getTenant: async (signal?: AbortSignal): Promise<TenantWorkScheduleDto> => {
    if (USE_MOCK) return { ...tenantWorkScheduleMock };

    const url = `${API_V1}/work-schedules/tenant`;
    const { data } = await (signal
      ? httpClient.get<TenantWorkScheduleDto>(url, { signal })
      : httpClient.get<TenantWorkScheduleDto>(url));
    return data;
  },

  /**
   * Creates or updates the tenant work schedule.
   * PUT /api/work-schedules/tenant
   */
  upsertTenant: async (payload: UpsertTenantWorkSchedulePayload): Promise<void> => {
    if (USE_MOCK) {
      tenantWorkScheduleMock.segments = payload.segments;
      tenantWorkScheduleMock.workingDays = payload.workingDays;
      tenantWorkScheduleMock.isDefault = false;
      return;
    }

    await httpClient.put<UpsertTenantWorkSchedulePayload, void>(
      `${API_V1}/work-schedules/tenant`,
      payload,
    );
  },

  /**
   * Returns the work schedule override for a member, or `null` when the member
   * has no override (404 → inherits the tenant schedule).
   * GET /api/work-schedules/members/{userId}
   */
  getMember: async (
    userId: string,
    signal?: AbortSignal,
  ): Promise<MemberWorkScheduleDto | null> => {
    if (USE_MOCK) {
      return memberWorkSchedulesMock.find((m) => m.userId === userId) ?? null;
    }

    try {
      const url = `${API_V1}/work-schedules/members/${userId}`;
      const { data } = await (signal
        ? httpClient.get<MemberWorkScheduleDto>(url, { signal })
        : httpClient.get<MemberWorkScheduleDto>(url));
      return data;
    } catch (error: unknown) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  },

  /**
   * Returns paginated member work schedule overrides for the tenant.
   * GET /api/work-schedules/members
   */
  getMemberSchedules: async (
    params: MemberWorkSchedulesQueryParams,
    signal?: AbortSignal,
  ): Promise<MemberWorkSchedulesResponse> => {
    if (USE_MOCK) {
      let items = [...memberWorkSchedulesMock];

      if (params.SearchTerm) {
        const term = params.SearchTerm.toLowerCase();
        items = items.filter((m) => (m.userId ?? '').toLowerCase().includes(term));
      }

      const page = params.Page ?? 1;
      const pageSize = params.PageSize ?? 10;
      const total = items.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const start = (page - 1) * pageSize;

      return { items: items.slice(start, start + pageSize), total, totalPages };
    }

    const { data } = await httpClient.get<MemberWorkScheduleDtoPagedResult>(
      `${API_V1}/work-schedules/members`,
      { params, signal },
    );

    return {
      items: data.items ?? [],
      total: data.totalCount,
      totalPages: data.totalPages,
    };
  },

  /**
   * Creates or updates the work schedule override for a member.
   * PUT /api/work-schedules/members/{userId}
   */
  upsertMember: async (userId: string, payload: UpsertMemberWorkSchedulePayload): Promise<void> => {
    if (USE_MOCK) {
      const existing = memberWorkSchedulesMock.find((m) => m.userId === userId);
      if (existing) {
        existing.segments = payload.segments;
        existing.updatedAt = '2026-06-19T00:00:00Z';
      } else {
        memberWorkSchedulesMock.push({
          tenantId: 'mock-tenant',
          userId,
          hoursPerDay: 0,
          createdAt: '2026-06-19T00:00:00Z',
          updatedAt: null,
          segments: payload.segments,
        });
      }
      return;
    }

    await httpClient.put<UpsertMemberWorkSchedulePayload, void>(
      `${API_V1}/work-schedules/members/${userId}`,
      payload,
    );
  },

  /**
   * Deletes the work schedule override for a member.
   * DELETE /api/work-schedules/members/{userId}
   */
  removeMember: async (userId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = memberWorkSchedulesMock.findIndex((m) => m.userId === userId);
      if (index !== -1) memberWorkSchedulesMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/work-schedules/members/${userId}`);
  },
};
